// Catalog.ID Client — WebSocket Event Push
// ============================================================================
// Manages a persistent WebSocket connection to /ws/events for receiving
// real-time entity-change notifications from the server.

import type { Shell } from '@catalog-shell';
import { getActiveCid } from './device-storage';
import { apiBaseFor } from './origins';

/** Entity-to-data-changed-type mapping. */
const ENTITY_TYPE_MAP: Record<string, string> = {
    claim: 'claims',
    certificate: 'claims',       // certificate changes affect claim display
    publicclaim: 'publicclaims',
    trust: 'trust',
    account: 'account',
    delegate: 'delegates',
    agent: 'agents',             // covers agent, apikey, refresh token mutations
};

class EventSocket {
    private ws: WebSocket | null = null;
    private shell: Shell | null = null;
    private sessionToken: string | null = null;
    /** The identity the token belongs to: the socket opens at ITS origin. */
    private cid: string | null = null;
    private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    private reconnectAttempts = 0;
    private intentionalClose = false;

    private static readonly MAX_RECONNECT_DELAY = 30_000;

    setShell(shell: Shell): void {
        this.shell = shell;
    }

    connect(token: string, cid?: string | null): void {
        this.sessionToken = token;
        this.cid = cid ?? getActiveCid();
        this.intentionalClose = false;
        this.reconnectAttempts = 0;
        this.openSocket();
    }

    disconnect(): void {
        this.intentionalClose = true;
        this.sessionToken = null;
        this.cid = null;
        this.cancelReconnect();
        if (this.ws) {
            this.ws.close(1000, 'logout');
            this.ws = null;
        }
    }

    isConnected(): boolean {
        return this.ws?.readyState === WebSocket.OPEN;
    }

    private openSocket(): void {
        if (!this.sessionToken) return;

        // Close any existing socket
        if (this.ws) {
            this.ws.onclose = null;
            this.ws.close();
            this.ws = null;
        }

        // The socket lives where the session does: the identity's origin
        // (docs/cid-governance.md D18), the same server api/client.ts sends its
        // requests to. In dashboard mode the iframe origin is the helper service
        // (e.g. localhost:61534), which does NOT host /ws/events, so the entry
        // node is the operator directory's (the helper's runtime-config); an
        // origin elsewhere is its own absolute URL. Standalone with the origin at the entry node the base is
        // empty and we fall back to a same-origin URL.
        const apiBase = apiBaseFor(this.cid);
        let url: string;
        if (apiBase) {
            url = apiBase.replace(/^http/, 'ws') + '/ws/events';
        } else {
            const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
            url = `${protocol}//${window.location.host}/ws/events`;
        }
        this.ws = new WebSocket(url);

        this.ws.onopen = () => {
            // Send auth message
            this.ws!.send(JSON.stringify({ type: 'auth', token: this.sessionToken }));
        };

        this.ws.onmessage = (event) => {
            this.handleMessage(event.data as string);
        };

        this.ws.onclose = () => {
            this.ws = null;
            if (!this.intentionalClose) {
                this.scheduleReconnect();
            }
        };

        this.ws.onerror = () => {
            // onclose will fire after onerror — reconnect happens there
        };
    }

    private handleMessage(data: string): void {
        try {
            const msg = JSON.parse(data) as { type: string; entity?: string; id?: string; event?: string };

            if (msg.type === 'authenticated') {
                this.reconnectAttempts = 0;
                return;
            }

            if (msg.type === 'entity-change' && msg.entity) {
                const dataType = ENTITY_TYPE_MAP[msg.entity] ?? msg.entity;
                this.shell?.emit('data-changed', {
                    type: dataType,
                    entity: msg.entity,
                    id: msg.id,
                    event: msg.event,
                });
            }
        } catch {
            // ignore malformed messages
        }
    }

    private scheduleReconnect(): void {
        this.cancelReconnect();
        const delay = Math.min(
            1000 * Math.pow(2, this.reconnectAttempts),
            EventSocket.MAX_RECONNECT_DELAY,
        );
        this.reconnectAttempts++;
        this.reconnectTimer = setTimeout(() => this.openSocket(), delay);
    }

    private cancelReconnect(): void {
        if (this.reconnectTimer !== null) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }
    }
}

export const eventSocket = new EventSocket();
